import * as React from "react";
import {CnrDto, ModelyearDto, TypefamilyDto} from "../../../services/Client";
import {SelectedValue} from "../../../components/SelectField";
import Button from "../../../components/Button";
import Dialog from "../../../components/Dialogs/Dialog";
import {ITypefamilyMap} from "../../../redux/reducers/generalReducer";
import {GroupsUtils} from "./GroupsUtils";
import {Checkbox} from "semantic-ui-react";

interface IViewAccessRightsDialogProps {
    /** Name dentifier for group */
    identifier: string;

    /** Already assigned model years */
    assignedModelyears: ModelyearDto[];

    /** Already assigned type families */
    assignedTypefamilies: TypefamilyDto[];

    /** Already assigned cnrs */
    assignedCnrs: CnrDto[];

    /** All available modelyears that may be assigned to the users. */
    modelyears: ModelyearDto[];

    /** All available typefamilies that may be assigned to the users. */
    typefamiliesMap: ITypefamilyMap;

    /** All available cnrs that may be assigned to the users. */
    cnrs: CnrDto[];

    /** Translation function */
    t: reactI18Next.TranslationFunction;

    /** Action on close dialog */
    onClose: () => void;

    /** Action on save */
    onSave: (modelyears: ModelyearDto[], typefamilies: TypefamilyDto[], cnrs: CnrDto[]) => void;
}

interface IViewAccessRightsDialogState {
    assignedModelyears: ModelyearDto[];
    assignedTypefamilies: string[];
    assignedCnrs: CnrDto[];
}

export class ViewAccessRightsDialog extends React.Component<IViewAccessRightsDialogProps, IViewAccessRightsDialogState>  {

    constructor(props: IViewAccessRightsDialogProps) {
        super(props);

        this.state = {
            assignedModelyears: [],
            assignedTypefamilies: [],
            assignedCnrs: []
        };
    }

    componentDidMount() {
        this.setAssignedRights();
    }

    componentDidUpdate(prevProps: IViewAccessRightsDialogProps) {
        if (prevProps !== this.props) {
            this.setAssignedRights();
        }
    }

    setAssignedRights() {
        this.setState({
            assignedModelyears: this.props.assignedModelyears,
            assignedTypefamilies: GroupsUtils.mapAssignedTypefamilies(this.props.assignedTypefamilies),
            assignedCnrs: this.props.assignedCnrs
        });
    }

    renderAssignedCnrs = (cnrDataSource: CnrDto[], cnrValues: CnrDto[], allSelected: boolean): JSX.Element => (
        <div className={'display--flex margin-bottom--10px'}>
            {GroupsUtils.renderSelectField(
                this.props.t("CNRS"),
                this.props.t("C_NUMBER"),
                cnrDataSource,
                cnrValues,
                (cnr: CnrDto) => this.props.t(`GLOBAL:${cnr.cnr}`),
                true,
                (actual: CnrDto, expected: CnrDto) => actual.objectId === expected.objectId,
                {width: '90%'},
                (selectedValue: Array<SelectedValue>) =>
                    this.setState({assignedCnrs: selectedValue.map(value => value.selectedValue as CnrDto)})
            )}

            {this.getSelectAllBtn(
                allSelected,
                () => this.setState({assignedCnrs: allSelected ? [] : cnrDataSource})
            )}
        </div>
    )

    renderAssignedModelyears = (modelyearDataSource: ModelyearDto[], modelyearValues: ModelyearDto[], allSelected: boolean): JSX.Element => (
        <div className={'display--flex margin-bottom--10px'}>
            {GroupsUtils.renderSelectField(
                this.props.t("MODELYEARS"),
                this.props.t("MODELYEAR"),
                modelyearDataSource,
                modelyearValues,
                (modelyear: ModelyearDto) => modelyear.mj4 + '(' + modelyear.mj1 + ')',
                true,
                (actual: ModelyearDto, expected: ModelyearDto) => actual.objectId === expected.objectId,
                {width: '90%'},
                (selectedValue: Array<SelectedValue>) => {
                    const newModelyears = selectedValue.map(value => value.selectedValue as ModelyearDto);
                    // update also selected typefamilies in case a year was removed and typefamily is not available
                    const newTypefamilyDS = GroupsUtils.getTypefamilyDataSource(this.props.typefamiliesMap, newModelyears);
                    const newTypefamilyValues = this.state.assignedTypefamilies.filter(tf => newTypefamilyDS.find(tfDS => tfDS === tf) !== undefined);

                    this.setState({assignedModelyears: newModelyears, assignedTypefamilies: newTypefamilyValues});
                }
            )}

            {this.getSelectAllBtn(
                allSelected,
                () => allSelected
                    ? this.setState({assignedModelyears: [], assignedTypefamilies: []})
                    : this.setState({assignedModelyears: modelyearDataSource})
            )}
        </div>
    )

    renderAssignedTypefamilies = (typefamilyDataSource: string[], typefamilyValues: string[], allSelected: boolean): JSX.Element => (
        <div className={'display--flex margin-bottom--10px'}>
            {GroupsUtils.renderSelectField(
                this.props.t("TYPEFAMILIES"),
                this.props.t("TYPEFAMILY"),
                typefamilyDataSource,
                typefamilyValues,
                (tf: String) => this.props.t("GLOBAL:" + tf),
                true,
                undefined,
                {width: '90%'},
                (selectedValue: Array<SelectedValue>) =>
                    this.setState({assignedTypefamilies: selectedValue.map(value => value.selectedValue as string)}),
                false,
                false
            )}

            {this.getSelectAllBtn(
                allSelected,
                () => this.setState({assignedTypefamilies: allSelected ? [] : typefamilyDataSource})
            )}
        </div>
    )

    getSelectAllBtn = (checked: boolean, onChange: () => void) => (
        <div style={{padding: '0px 20px'}}>
            <b>{this.props.t("ALL")}: </b>
            <Checkbox
                toggle={true}
                checked={checked}
                onChange={onChange}
            />
        </div>
    )

    onSave = () => {
        const {typefamiliesMap} = {... this.props};

        let assignedTypefamilies: TypefamilyDto[] = [];
        if (this.state.assignedTypefamilies.length > 0) {
            this.state.assignedModelyears.forEach(modelyear => {
                this.state.assignedTypefamilies.forEach(tf => {
                    const elements: TypefamilyDto[] =
                        typefamiliesMap &&
                            typefamiliesMap[modelyear.mj1] ?
                            typefamiliesMap[modelyear.mj1].elements as TypefamilyDto[] : [];

                    const tfElem = elements.find(item => item.key === tf);
                    if (tfElem) {
                        assignedTypefamilies.push(tfElem);
                    }
                });
            });
        }

        this.props.onSave(this.state.assignedModelyears, assignedTypefamilies, this.state.assignedCnrs);
    }

    render() {
        const {cnrs, modelyears, typefamiliesMap} = {... this.props};
        const {assignedCnrs, assignedModelyears, assignedTypefamilies} = {... this.state};
        const typefamilies = GroupsUtils.getTypefamilyDataSource(typefamiliesMap, assignedModelyears);

        return (
            <Dialog
                title={this.props.t("EDIT_VIEW_ACCESS_RIGHTS") + ': ' + this.props.identifier}
                open={true}
                fullWidth={false}
                actions={
                    [
                        <Button
                            key={'save'}
                            icon={'save'}
                            onClick={this.onSave}
                        />,
                        <Button
                            key={'close'}
                            icon={'close'}
                            onClick={this.props.onClose}
                        />
                    ]
                }
            >
                <>
                    {this.renderAssignedCnrs(
                        cnrs,
                        assignedCnrs,
                        cnrs.length > 0 &&
                        cnrs.length === assignedCnrs.length &&
                        cnrs.every((cnrDS) => assignedCnrs.find(cnrVal => cnrVal.objectId === cnrDS.objectId) !== undefined)
                    )}

                    {this.renderAssignedModelyears(
                        modelyears,
                        assignedModelyears,
                        modelyears.length > 0 &&
                        modelyears.length === assignedModelyears.length &&
                        modelyears.every((myDS) => assignedModelyears.find(myVal => myVal.objectId === myDS.objectId) !== undefined)
                    )}

                    {this.renderAssignedTypefamilies(
                        typefamilies,
                        assignedTypefamilies,
                        typefamilies.length > 0 &&
                        typefamilies.length === assignedTypefamilies.length &&
                        typefamilies.every((tfDS) => assignedTypefamilies.find(tfVal => tfVal === tfDS) !== undefined)
                    )}
                </>
            </Dialog >
        );
    }
}



// WEBPACK FOOTER //
// ./src/views/AdminView/AccessTab/ViewAccessRightsDialog.tsx